import { useState, useEffect } from 'react';
import './Countdown.scss';

interface CountdownProps {
  targetTimestamp: number | bigint;
  onExpire?: () => void;
  className?: string;
}

export default function Countdown({ targetTimestamp, onExpire, className = '' }: CountdownProps) {
  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    const target = Number(targetTimestamp);

    const calculateTimeLeft = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = target - now;

      if (remaining <= 0) {
        setTimeLeft(0);
        if (onExpire) onExpire();
        return 0;
      }

      return remaining;
    };

    // Initial calculation
    const initialRemaining = calculateTimeLeft();
    setTimeLeft(initialRemaining);

    if (initialRemaining <= 0) return;

    // Interval
    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTimestamp, onExpire]);

  const formatTime = (seconds: number) => {
    if (seconds <= 0) return 'Closed';

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    if (h > 24) {
      const d = Math.floor(h / 24);
      return `${d}d ${h % 24}h`;
    }

    return `${h}h ${m}m ${s}s`;
  };

  const getUrgencyClass = (seconds: number) => {
    if (seconds <= 0) return 'countdown--expired';
    if (seconds < 3600) return 'countdown--critical'; // < 1 hour
    if (seconds < 86400) return 'countdown--urgent'; // < 24 hours
    return '';
  };

  return (
    <span className={`countdown ${getUrgencyClass(timeLeft)} ${className}`}>
      {formatTime(timeLeft)}
    </span>
  );
}
